import React, { useCallback, useEffect, useRef } from 'react';
import './StepperInput.css';

/**
 * F0 — Generic stepper control for the appointments-redesign cards.
 *
 * Renders: [− <mono value> +]
 *
 * Props:
 *   - value: current numeric value (number or null)
 *   - min, max: clamp range
 *   - step: increment (default 1)
 *   - onChange: (next: number) => void
 *   - onBlur: optional, fires after the inline input loses focus or after a
 *             stepper button release. Receives the final clamped value.
 *   - disabled
 *   - ariaLabel
 *   - allowTyping: when true, the displayed value is an inline input the
 *                  user can type into. Defaults to true.
 *
 * Click-and-hold accelerates the step (60ms cadence, capped). Cleans up
 * its own timer on release / blur / leave / disable. Respects
 * `prefers-reduced-motion` by skipping the press-scale animation.
 */
const HOLD_CADENCE_MS = 60;
const HOLD_DELAY_MS = 350;

function clampValue(value, min, max) {
  if (typeof value !== 'number' || Number.isNaN(value)) return min ?? 0;
  if (typeof min === 'number' && value < min) return min;
  if (typeof max === 'number' && value > max) return max;
  return value;
}

const StepperInput = ({
  value,
  min = 0,
  max = 999,
  step = 1,
  onChange,
  onBlur,
  disabled = false,
  ariaLabel,
  allowTyping = true,
  variant = 'default',
}) => {
  const timerRef = useRef(null);
  const delayRef = useRef(null);
  const latestRef = useRef(value);

  useEffect(() => {
    latestRef.current = value;
  }, [value]);

  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (delayRef.current) {
      clearTimeout(delayRef.current);
      delayRef.current = null;
    }
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);
  useEffect(() => {
    if (disabled) clearTimers();
  }, [disabled, clearTimers]);

  const applyDelta = useCallback(
    (delta) => {
      if (disabled) return;
      const current = typeof latestRef.current === 'number' ? latestRef.current : min;
      const next = clampValue(current + delta, min, max);
      if (next !== current) {
        onChange?.(next);
      }
    },
    [disabled, max, min, onChange]
  );

  const startHold = useCallback(
    (delta) => {
      if (disabled) return;
      applyDelta(delta);
      delayRef.current = setTimeout(() => {
        timerRef.current = setInterval(() => applyDelta(delta), HOLD_CADENCE_MS);
      }, HOLD_DELAY_MS);
    },
    [applyDelta, disabled]
  );

  const stopHold = useCallback(() => {
    clearTimers();
    onBlur?.(latestRef.current);
  }, [clearTimers, onBlur]);

  const handleTypedChange = (rawValue) => {
    if (rawValue === '') {
      onChange?.(null);
      return;
    }
    const parsed = parseInt(rawValue, 10);
    onChange?.(Number.isNaN(parsed) ? null : parsed);
  };

  const handleInputBlur = () => {
    const clamped = clampValue(typeof value === 'number' ? value : min, min, max);
    if (clamped !== value) onChange?.(clamped);
    onBlur?.(clamped);
  };

  const displayValue = value == null ? '' : String(value);

  return (
    <div
      className={`appointments-v3-stepper appointments-v3-stepper--${variant} ${disabled ? 'is-disabled' : ''}`}
      role="group"
      aria-label={ariaLabel}
    >
      <button
        type="button"
        className="appointments-v3-stepper-btn"
        aria-label={`Decrease ${ariaLabel || 'value'}`}
        disabled={disabled || (typeof min === 'number' && value <= min)}
        onMouseDown={() => startHold(-step)}
        onMouseUp={stopHold}
        onMouseLeave={stopHold}
        onTouchStart={() => startHold(-step)}
        onTouchEnd={stopHold}
        onBlur={stopHold}
      >
        −
      </button>
      {allowTyping ? (
        <input
          type="number"
          className="appointments-v3-stepper-value"
          value={displayValue}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          aria-label={ariaLabel}
          onChange={(e) => handleTypedChange(e.target.value)}
          onBlur={handleInputBlur}
        />
      ) : (
        <span className="appointments-v3-stepper-value">{displayValue}</span>
      )}
      <button
        type="button"
        className="appointments-v3-stepper-btn"
        aria-label={`Increase ${ariaLabel || 'value'}`}
        disabled={disabled || (typeof max === 'number' && value >= max)}
        onMouseDown={() => startHold(step)}
        onMouseUp={stopHold}
        onMouseLeave={stopHold}
        onTouchStart={() => startHold(step)}
        onTouchEnd={stopHold}
        onBlur={stopHold}
      >
        +
      </button>
    </div>
  );
};

export default StepperInput;
